import {TimeEntryComponent} from '../ui/time-entry/time-entry.component';
import {CalendarActual, emptyCalendar, readWorkforceCalendar} from '../workforce-v2/workforce-calendar';
import {EmployeeDurationPipe} from '../employee/employee-duration.pipe';
import { IonModal } from '@ionic/angular';
import { ModalShellComponent } from '../ui/modal-shell/modal-shell.component';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormsModule } from '@angular/forms';
import { WorkforceService } from '../workforce-v2/workforce.service';
import { PlanningPanelComponent } from '../workforce-v2/planning-panel.component';
import { CommonModule } from '@angular/common';
import { Component, computed, EnvironmentInjector, HostListener, inject, signal, ChangeDetectionStrategy, effect } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { fieldSorter } from '@scandium-oy/ngx-scandium';
import { addDays, addWeeks, format, hoursToMilliseconds, isBefore, startOfToday, startOfWeek } from 'date-fns';
import { minutesToSeconds } from 'date-fns/minutesToSeconds';
import { secondsToMilliseconds } from 'date-fns/secondsToMilliseconds';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { combineLatest, firstValueFrom } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { AppCommonModule } from '../common.module';
import { NoResultsComponent } from '../components/no-results/no-results.component';
import { Schedule } from '../models/schedule.model';
import { SubItem } from '../models/sub-item.model';
import { Tag } from '../models/tag.model';
import { ContractorsService } from '../services/contractors.service';
import { SitesService } from '../services/sites.service';
import { TagsService } from '../services/tags.service';
import { UsersService } from '../services/users.service';
import { EmployeeWorkspaceService } from '../users/workspace/employee-workspace.service';
import { defaultFullHours, mobileWidth } from '../utility/constants';
import { convertTimestamp, millisToHours, numberOfWeek } from '../utility/time';

function getCurrentLevelingPeriod(selectedDate: Date, levelingStart: Date, levelingPeriodWeeks: number) {
  const civil = (d: Date)=>Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const offset = Math.floor((civil(selectedDate) - civil(levelingStart)) / (levelingPeriodWeeks * 7 * 86400000));
  const start = addWeeks(levelingStart, offset * levelingPeriodWeeks);
  return {start, end:addDays(addWeeks(start, levelingPeriodWeeks), -1)};
}

@Component({
  selector: 'app-shift',
  templateUrl: './shift.component.html',
  styleUrls: ['./shift.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TimeEntryComponent,
    IonModal, EmployeeDurationPipe,
    ModalShellComponent,
    FormsModule,
    PlanningPanelComponent,
    CommonModule,
    AppCommonModule,
    NoResultsComponent,
    NgCircleProgressModule,
  ],
})
export class ShiftComponent {
  private contractorsService = inject(ContractorsService);
  private employeeWorkspaceService = inject(EmployeeWorkspaceService);
  private injectorContext = inject(EnvironmentInjector);
  private sitesService = inject(SitesService);
  private tagsService = inject(TagsService);
  private usersService = inject(UsersService);

  private selectedDayS = signal<Date>(startOfWeek(startOfToday(), { weekStartsOn: 1 }));
  mobileView = signal(false);

  days = computed(() => {
    this.totals();
    const contractor = this.contractorsService.contractorS();
    if (contractor != null) {
      return this.getDays(this.mobileView(), this.selectedDayS());
    } else {
      return [];
    }
  });

  filterArea = signal<Tag[]>([]);
  filterTeam = signal<Tag[]>([]);
  hasDrafts = signal(false);
  selectedCellKey = signal<string | null>(null);
  hasPendingChanges = computed(() => this.hasDrafts());

  weekLabel = computed(() => `${numberOfWeek(this.selectedDayS())}`);
  yearLabel = computed(() => `${this.selectedDayS().getFullYear()}`);

  private api = inject(WorkforceService);
  rows = signal<any[]>([]);
  calendar = signal(emptyCalendar());
  displayRows = computed(()=>[...this.calendar().planned, ...this.rows().filter(r=>r.status === 'draft' && !r.cancelled && !this.calendar().planned.some(p=>p.id === r.id))]);
  actualDetail = signal<CalendarActual | null>(null);
  actualProjectName = signal('—');
  actualDetailEntries = computed(()=>{
    const selected = this.actualDetail();
    return selected ? this.calendar().actual.filter(a=>a.workerId === selected.workerId && a.date === selected.date) : [];
  });

  actualDuration(entries: CalendarActual[]){
    const minutes = Math.round(entries.reduce((total, entry)=>total + Number(entry.minutes), 0));
    return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')} h`;
  }

  actualFor(date: Date, worker: string){
return this.calendar().actual.filter(a=>a.workerId === worker && a.date === format(date, 'yyyy-MM-dd'));
}

  siteName(id: string){
return this.options().sites.find(s=>s.id === id)?.name || id;
}

  async openActual(entry: CalendarActual){
    this.actualDetail.set(entry);this.actualProjectName.set('—');
    if (!entry.projectId) {
return;
}
    try {
const result = await this.api.request<{projects: {name: string}[]}>('options?' + new URLSearchParams({scope:'project-label', projectSite:entry.siteId, project:entry.projectId}).toString());
      if (this.actualDetail() === entry) {
this.actualProjectName.set(result.projects[0]?.name || '—');
}
    } catch {/* Missing labels must not hide recorded work. */}
  }

  options = signal<any>({workers:[], sites:[]});
  absences = signal<any[]>([]);
  error = signal(false);
  busy = signal(false);
  editor = signal<{employee: string;date: string;shift: any} | null>(null);
  review = signal(false);
  publishWeeks = 4;
  planningDate(){
return format(this.selectedDayS(), 'yyyy-MM-dd');
}

  setPlanningDate(value: string){
if (/^\d{4}-\d{2}-\d{2}$/.test(value)){
this.selectedDayS.set(startOfWeek(new Date(value + 'T12:00:00'), {weekStartsOn:1}));this.review.set(false);
}
}

  publicationKey = '';
  totals = signal<any>(null);
  policyWeekly = 2400;policyStart = format(startOfWeek(new Date(), {weekStartsOn:1}), 'yyyy-MM-dd');policyWeeks = 4;
  async savePolicy(){
this.error.set(false);try {
await this.api.request('planning-policy', 'POST', {weekly_minutes:this.policyWeekly, balancing_start:this.policyStart, balancing_weeks:this.policyWeeks});await this.reload();
} catch {
this.error.set(true);
}
}

  private generation = 0;
  items$ = combineLatest([toObservable(this.displayRows), toObservable(this.calendar), toObservable(this.options), toObservable(this.filterArea), toObservable(this.filterTeam)]).pipe(map(([rows, calendar, options, areas, teams])=>{
    const workers = new Map(options.workers.map(w=>[w.id, w]));
    for (const id of [...rows.map(r=>r.firestore_user_id), ...calendar.actual.map(a=>a.workerId)]) {
if (!workers.has(id)){
workers.set(id, {id, name:id});
}
}
    return [...workers.values()].filter((w: any)=>(!areas.length || areas.some(a=>a.users.some(u=>u.guid === w.id))) && (!teams.length || teams.some(t=>t.users.some(u=>u.guid === w.id)))).map((w: any)=>({guid:w.id, name:w.name, fullHours:false, areas:'', teams:'', schedules:rows.filter(r=>r.firestore_user_id === w.id && !r.cancelled).map(r=>this.asSchedule(r))}));
  }));

  private asSchedule(r: any): Schedule {
    const day = new Date(r.shift_date + 'T12:00:00');
    return {guid:r.id, contractor:'', date:day.toLocaleDateString('fi'), dateObj:day, users:[r.firestore_user_id], site:r.firestore_site_id, machines:[], machinesGuids:[], projects:[], times:[{user:r.firestore_user_id, start:r.start_time ? new Date(r.shift_date + 'T' + r.start_time) : null, end:r.end_time ? new Date(r.shift_date + 'T' + r.end_time) : null, status:r.status}]};
  }

  async reload() {
    const generation = ++this.generation;
    const start = format(this.selectedDayS(), 'yyyy-MM-dd'), end = format(addDays(this.selectedDayS(), 27), 'yyyy-MM-dd');
    this.error.set(false);
    try {
      const [rows, options, absences, totals, calendar] = await Promise.all([this.api.request<any[]>(`shifts?start=${start}&end=${end}`), this.api.request<any>('options?scope=filters'), this.api.request<any[]>(`absences?start=${start}&end=${end}`), this.api.request<any>(`planning-totals?start=${start}&end=${end}`), readWorkforceCalendar(this.api, start, end)]);
      if (generation !== this.generation) {
return;
}
      this.rows.set(rows);this.calendar.set(calendar);this.actualDetail.set(null);this.options.set(options);this.absences.set(absences);this.totals.set(totals);
      if (totals.policy){
this.policyWeekly = totals.policy.weekly_minutes;this.policyStart = totals.policy.balancing_start;this.policyWeeks = totals.policy.balancing_weeks;
}
      this.hasDrafts.set(rows.some(r=>r.status === 'draft'));
    } catch {
if (generation === this.generation) {
this.error.set(true);
}
}
  }

  pendingRows(){
return this.rows().filter(r=>r.status === 'draft' && r.shift_date <= format(addDays(this.selectedDayS(), this.publishWeeks * 7 - 1), 'yyyy-MM-dd'));
}

  name(id: string){
return this.options().workers.find(w=>w.id === id)?.name || id;
}

  async publish(){
    if (this.busy()) {
return;
} this.busy.set(true);this.error.set(false);
    try {
await this.api.request('shifts/publish', 'POST', {request_id:this.publicationKey, start:format(this.selectedDayS(), 'yyyy-MM-dd'), end:format(addDays(this.selectedDayS(), this.publishWeeks * 7 - 1), 'yyyy-MM-dd'), revisions:this.pendingRows().map(r=>({id:r.id, revision:r.draft_revision}))});this.review.set(false);await this.reload();
} catch {
this.error.set(true);
} finally {
this.busy.set(false);
}
  }

  saved(){
this.editor.set(null);void this.reload();
}

  areas$ = this.contractorsService.getCurrentContractor().pipe(
    filter((contractor) => contractor != null),
    switchMap((contractor) => this.tagsService.getList({ contractor: contractor.guid, type: 'area' })),
    map((items) => items.sort(fieldSorter(['name']))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  teams$ = this.contractorsService.getCurrentContractor().pipe(
    filter((contractor) => contractor != null),
    switchMap((contractor) => this.tagsService.getList({ contractor: contractor.guid, type: 'team' })),
    map((items) => items.sort(fieldSorter(['name']))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  roleS = computed(() => this.usersService.role());

  private getDays(mobileView: boolean, selectedDay: Date) {
    const days: { date: Date; dateString: string; isToday: boolean; isWeekend: boolean; isPeriodStart: boolean; isPeriodEnd: boolean }[] = [];

    const levelingPeriodStart = new Date(this.policyStart + 'T00:00:00');
    const levelingPeriodWeeks = this.policyWeeks;

    const maxDate = mobileView ? addDays(selectedDay, 7) : addWeeks(selectedDay, 2);

    const period = getCurrentLevelingPeriod(selectedDay, levelingPeriodStart, levelingPeriodWeeks);

    let currentDate = selectedDay;
    while (isBefore(currentDate, maxDate)) {
      const item = {
        date: currentDate,
        dateString: currentDate.toLocaleDateString('fi'),
        isToday: currentDate.toISOString() === startOfToday().toISOString(),
        isWeekend: currentDate.getDay() === 0 || currentDate.getDay() === 6,
        isPeriodStart: currentDate.toLocaleDateString('fi') === period.start.toLocaleDateString('fi'),
        isPeriodEnd: currentDate.toLocaleDateString('fi') === period.end.toLocaleDateString('fi'),
      };
      days.push(item);
      currentDate = addDays(currentDate, 1);
    }

    return days;
  }

  constructor() {
    this.onResize();
    effect(()=>{
this.selectedDayS();void this.reload();
});
  }

  @HostListener('window:resize')
  onResize() {
    this.mobileView.set(window.innerWidth < mobileWidth);
  }

  getItem(date: string, user: string, allSchedules: Schedule[]) {
    const fullHours = this.contractorsService.contractorS().usersOptions?.workingHours ?? defaultFullHours;
    const schedules = allSchedules?.filter((it) => it.date === date) ?? [];
    if (schedules.length > 0) {
      const times = schedules.map((it) => it.times.find((itt) => itt.user === user));
      const lunchBreak = this.contractorsService.contractorS().usersOptions?.paidLunchBreak ? 0 : 30;
      const lunchBreakMs = secondsToMilliseconds(minutesToSeconds(lunchBreak));
      const durationsMs = schedules.reduce((total, schedule)=>total + Number(this.displayRows().find(r=>r.id === schedule.guid)?.minutes || 0) * 60000, 0);
      let fullHoursMs = hoursToMilliseconds(fullHours);
      fullHoursMs -= lunchBreakMs;
      const percentage = (durationsMs / fullHoursMs) * 100;
      const knownMinutes = schedules.some(s=>this.displayRows().find(r=>r.id === s.guid)?.minutes != null);
      const hasPublished = times.some(t=>t?.status === 'published');
      const timeStatus = times.filter((t) => t != null).every((it) => it.status === 'published') ? 'published' : 'draft';
      const details = schedules.map((it) => {
        const site = this.sitesService.getSync(it.site);
        const time = it.times.filter((itt) => itt.user === user);
        return { site: site?.name ?? it.site, start: time[0].start, end: time[0].end };
      }).sort((a, b) => (convertTimestamp(a.start)?.getTime() || 0) - (convertTimestamp(b.start)?.getTime() || 0));
      return { hasPlanned:true, hasPublished, absence: schedules.some((it) => it.absence), timeStatus, hours: knownMinutes ? millisToHours(durationsMs) : null, percentage, details };
    }
    return { hasPlanned:false, hasPublished:false, absence: this.absences().some(a=>a.firestore_user_id === user && this.days().some(d=>d.dateString === date && format(d.date, 'yyyy-MM-dd') >= a.start_date && format(d.date, 'yyyy-MM-dd') <= a.end_date)), timeStatus: null, hours: null, details: [] };
  }

  onDateChange(date: Date) {
    this.selectedDayS.set(date);
  }

  previousWeek(): void {
    this.selectedDayS.update((date) => addWeeks(date, -1));
    this.selectedCellKey.set(null);
  }

  nextWeek(): void {
    this.selectedDayS.update((date) => addWeeks(date, 1));
    this.selectedCellKey.set(null);
  }

  goToCurrentWeek(): void {
    this.selectedDayS.set(startOfWeek(startOfToday(), { weekStartsOn: 1 }));
    this.selectedCellKey.set(null);
  }

  getCellKey(date: Date, userGuid: string): string {
    return `${date.toLocaleDateString('fi')}::${userGuid}`;
  }

  isCellSelected(date: Date, userGuid: string): boolean {
    return this.selectedCellKey() === this.getCellKey(date, userGuid);
  }

  onUser(item: SubItem) {
    void this.employeeWorkspaceService.open(item.guid, { initialTab: 'schedule' });
  }

  onAreaFilter(value: string): void {
    firstValueFrom(this.areas$).then((items) => {
      const selected = items.find((item) => item.guid === value);
      this.filterArea.set(selected ? [selected] : []);
    });
  }

  onTeamFilter(value: string): void {
    firstValueFrom(this.teams$).then((items) => {
      const selected = items.find((item) => item.guid === value);
      this.filterTeam.set(selected ? [selected] : []);
    });
  }

  onItem(date: Date, user: SubItem, _allSchedules: Schedule[]) {
    const iso = format(date, 'yyyy-MM-dd');
    this.editor.set({employee:user.guid, date:iso, shift:this.rows().find(r=>r.firestore_user_id === user.guid && r.shift_date === iso) || null});
  }

  async openPublishReview(): Promise<void> {
    this.publicationKey = crypto.randomUUID();this.review.set(true);
  }

  getShiftTooltip(
    schedule: {
      details: { site: string; start: Date | { toDate: () => Date }; end: Date | { toDate: () => Date } }[];
      hours: { hours: number; minutes: number } | null;
    },
    item: { teams?: string; areas?: string },
  ): string | null {
    if (schedule.details.length === 0) {
      return null;
    }

    const primaryDetail = schedule.details[0];
    const meta = [item.teams, item.areas].filter((value) => !!value).join(' • ');
    const hoursLabel = schedule.hours ? `${schedule.hours.hours},${schedule.hours.minutes} h` : '';
    return [
      `${this.formatTooltipTime(primaryDetail.start)} - ${this.formatTooltipTime(primaryDetail.end)}`,
      primaryDetail.site,
      meta,
      hoursLabel,
    ].filter((value) => !!value).join(' • ');
  }

  private formatTooltipTime(value: Date | { toDate: () => Date } | null): string {
    if (!value) {
return '—';
}
    const dateValue = value instanceof Date ? value : value.toDate();
    return `${dateValue.getHours().toString().padStart(2, '0')}:${dateValue.getMinutes().toString().padStart(2, '0')}`;
  }

}
